import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { AssignableUser, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { Alert, Button, Field, Select } from '../components/ui.js';

export function AssignmentPanel({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [assignee, setAssignee] = useState(workOrder.assignedToUserId ?? '');
  const [error, setError] = useState<string | null>(null);

  const mayAssign = can(PERMISSIONS.WORK_ORDER_ASSIGN);

  const { data: users } = useQuery({
    queryKey: ['reference', 'assignable-users'],
    queryFn: () =>
      apiRequest<{ users: AssignableUser[] }>('/api/reference/assignable-users').then(
        (result) => result.users,
      ),
    enabled: mayAssign,
  });

  const save = useMutation({
    mutationFn: (userId: string | null) =>
      apiRequest(`/api/work-orders/${workOrder.id}`, {
        method: 'PATCH',
        body: { assignedToUserId: userId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The assignment could not be saved. Please try again.',
      );
    },
  });

  if (!mayAssign) {
    return (
      <p className="text-sm text-slate-700">
        Assigned to <span className="font-medium">{workOrder.assignedToName ?? 'nobody yet'}</span>.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <Alert variant="error">{error}</Alert>}
      <Field label="Assigned to" htmlFor="assignee">
        <Select
          id="assignee"
          value={assignee}
          onChange={(event) => setAssignee(event.target.value)}
        >
          <option value="">Unassigned</option>
          {(users ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.fullName}
              {user.title ? ` — ${user.title}` : ''}
            </option>
          ))}
        </Select>
      </Field>
      <Button
        onClick={() => {
          setError(null);
          save.mutate(assignee === '' ? null : assignee);
        }}
        loading={save.isPending}
        disabled={assignee === (workOrder.assignedToUserId ?? '')}
      >
        Save assignment
      </Button>
    </div>
  );
}
